import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { LeadBuyerStateModel, OrganizationState } from 'remarketing-angular-library';

import {
  AuctionItemBidPlaced,
  AuctionItemBuyNowPlaced,
  AuctionItemOfferAdded,
  AuctionItemOfferUpdated,
  PurchaseStatus,
  PurchaseType,
  AuctionItemStatus as AuctionItemStatusValue,
} from './auction-item-status.interface';
import { BuyFee } from 'remarketing-angular-library/interfaces/buy-fee';

export type AuctionItemPayload = AuctionItemBidPlaced | AuctionItemBuyNowPlaced | AuctionItemOfferAdded | AuctionItemOfferUpdated;

export type AuctionItemStatus = {
  auctionItemStatus: string;
  purchaseStatus: string;
  leadBidderName: string;
  isOpeningPrice: boolean;
  offerAmount?: number;
  buyFee?: BuyFee
}

export type AuctionItemStatusType = keyof {
  auctionItemStatus,
  purchaseStatus,
}
export namespace AuctionItemStatusType {
  export const AuctionItem: AuctionItemStatusType = 'auctionItemStatus';
  export const Purchase: AuctionItemStatusType    = 'purchaseStatus';
}

@Injectable({ providedIn: 'root' })
export class AuctionItemStatusService {

  constructor(
    private _store: Store,
  ) { }

  get oeUuidOrganization(): string {
    return this._store.selectSnapshot( OrganizationState.oeUuid );
  }

  transform(item: AuctionItemPayload, prevItem?: any) {
    switch(item.purchaseType) {
      case PurchaseType.BidPlaced:
        return this.transformBidPlaced(item, prevItem);
      case PurchaseType.BuyNowPlaced:
        return this.transformBuyNowPlaced(item, prevItem);
      case PurchaseType.OfferAdded:
        return this.transformOfferAdded(item, prevItem );
      case PurchaseType.OfferUpdated:
        return this.transformOfferUpdated(item, prevItem);
      default:
        return this.transformDefault(item);
    }
  }

  transformWithLeadBuyerState(leadBuyerState: LeadBuyerStateModel, item: AuctionItemPayload, prevItem?: any) {
    if (item.purchaseType === PurchaseType.BuyNowPlaced) {
      return this.transformBuyNowPlacedLeadBuyer(leadBuyerState, item, prevItem);
    }

    return this.transform(item, prevItem);
  }

  transformBidPlaced(value: AuctionItemBidPlaced, prevValue?: any): AuctionItemStatus {
    const json = {
      leadBidderName: value.leadBidderName,
      isOpeningPrice: false,
    };

    const org = this.oeUuidOrganization;
    // Only want to update the owner of the bid list for lead bidder, so using lead buyer
    if ( org === value.leadBidderLeadBuyerOeUuid ) {
      return {
        ...json,
        auctionItemStatus: value.buyerAuctionItemStatus,
        purchaseStatus:    value.buyerPurchaseStatus
      };
    }
    else if ( org === value.outBidderOeUuid || prevValue.buyerPurchaseStatus === PurchaseStatus.Outbid) {
      return {
        ...json,
        auctionItemStatus: value.outbidAuctionItemStatus,
        purchaseStatus:    value.outbidPurchaseStatus
      };
    }
    else {
      const isAcceptingOffers = prevValue.buyerPurchaseStatus === PurchaseStatus.AcceptingOffers;
      return {
        ...json,
        auctionItemStatus: value.auctionItemStatus,
        purchaseStatus:    isAcceptingOffers ? value.purchaseStatus : prevValue.buyerPurchaseStatus
      };
    }
  }

  transformBuyNowPlaced(value: AuctionItemBuyNowPlaced, prevValue?: any): AuctionItemStatus {
    const json = {
      leadBidderName: null,
      isOpeningPrice: true,
    };

    if (this.oeUuidOrganization === value.buyerOeUuid) {
      return {
        ...json,
        auctionItemStatus: value.buyerAuctionItemStatus,
        purchaseStatus:    value.buyerPurchaseStatus,
        buyFee:            value.buyFee
      };
    } else {
      const isAcceptingOffers = prevValue.buyerPurchaseStatus === PurchaseStatus.AcceptingOffers;
      return {
        ...json,
        auctionItemStatus: value.auctionItemStatus,
        purchaseStatus:    isAcceptingOffers ? value.purchaseStatus : prevValue.buyerPurchaseStatus,
      };
    }
  }

  transformBuyNowPlacedLeadBuyer(leadBuyerState: LeadBuyerStateModel, value: AuctionItemBuyNowPlaced, prevValue?: any): AuctionItemStatus {
    const json = {
      leadBidderName: null,
      isOpeningPrice: true,
    };

    if (leadBuyerState
      && leadBuyerState.availableDealerships
      && !!leadBuyerState.availableDealerships.find(dealership => dealership.oeUuid === value.buyerOeUuid)
    ) {
      return {
        ...json,
        auctionItemStatus: value.buyerAuctionItemStatus,
        purchaseStatus:    value.buyerPurchaseStatus,
        buyFee:            value.buyFee
      };
    } else {
      const isAcceptingOffers = prevValue.buyerPurchaseStatus === PurchaseStatus.AcceptingOffers;
      return {
        ...json,
        auctionItemStatus: value.auctionItemStatus,
        purchaseStatus:    isAcceptingOffers ? value.purchaseStatus : prevValue.buyerPurchaseStatus,
      };
    }
  }

  transformOfferAdded(value: AuctionItemOfferAdded, prevValue?: any): AuctionItemStatus {
    const json = {
      leadBidderName: null,
      isOpeningPrice: true,
    };

    const org = this.oeUuidOrganization;
    if ( org === value.leadBuyerOeUuid ) {
      return {
        ...json,
        auctionItemStatus: value.buyerAuctionItemStatus,
        purchaseStatus:    value.buyerPurchaseStatus
      };
    }
    else if ( org === value.sellerOeUuid ) {
      return {
        ...json,
        auctionItemStatus: value.sellerAuctionItemStatus,
        purchaseStatus:    value.sellerPurchaseStatus
      };
    }
    else {
      return {
        ...json,
        auctionItemStatus: null,
        purchaseStatus:    null
      };
    }
  }

  transformOfferUpdated(value: AuctionItemOfferUpdated, prevValue?: any): AuctionItemStatus {
    let json;
    const org = this.oeUuidOrganization;

    if ( value.auctionItemStatus === AuctionItemStatusValue.BiddingInProgress ) {
      json  = {
        isOpeningPrice: false,
        leadBidderName: prevValue.highestBidderName
      };
    }
    else {
      json = {
        isOpeningPrice: true,
        leadBidderName: null
      };
    }

    const statusBuyNowOrBid = [ AuctionItemStatusValue.Won, AuctionItemStatusValue.Leading ];
    if ( statusBuyNowOrBid.includes( prevValue.buyerAuctionItemStatus ) ) {
      return {
        ...json,
        auctionItemStatus: prevValue.buyerAuctionItemStatus,
        purchaseStatus:    prevValue.buyerPurchaseStatus
      };
    }
    else if ( org === value.leadBuyerOeUuid ) {
      return {
        ...json,
        auctionItemStatus:  value.buyerAuctionItemStatus,
        purchaseStatus:     value.buyerPurchaseStatus,
        buyFee:             value.buyFee
      };
    }
    else if ( org === value.sellerOeUuid ) {
      return {
        ...json,
        auctionItemStatus: value.sellerAuctionItemStatus,
        purchaseStatus:    value.sellerPurchaseStatus
      };
    }
    else {
      const isAcceptingOffers = prevValue.buyerPurchaseStatus === PurchaseStatus.AcceptingOffers;
      return {
        ...json,
        auctionItemStatus: value.auctionItemStatus,
        purchaseStatus:    isAcceptingOffers ? value.purchaseStatus : prevValue.buyerPurchaseStatus,
        offerAmount:       prevValue.offerAmount
      };
    }
  }

  transformDefault(value: AuctionItemBidPlaced): AuctionItemStatus {
    return {
      isOpeningPrice:    true,
      leadBidderName:    value.leadBidderName,
      auctionItemStatus: null,
      purchaseStatus:    null,
    };
  }
}
